
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

type NavItem = {
  name: string;
  path: string;
};

interface MobileNavSheetProps {
  navItems: any[];
  isAuthenticated: boolean;
  logout: () => void;
  simplified?: boolean;
}

const MobileNavSheet = ({ navItems, isAuthenticated, logout, simplified = false }: MobileNavSheetProps) => {
  const location = useLocation();
  const isAuthPage = ["/login", "/register"].includes(location.pathname);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Don't show the mobile menu icon on auth pages
  if (isAuthPage) {
    return null;
  }
  
  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsLoggingOut(true);
    
    try {
      console.log("MobileNavSheet: Logout clicked");
      await logout();
      // Navigation is handled in the logout function
    } catch (error) {
      console.error("MobileNavSheet logout error:", error);
      toast.error("Failed to sign out, please try again");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const renderMobileNavContent = () => {
    if (simplified) {
      return (
        <div className="flex flex-col gap-4 pt-4 bg-gradient-to-b from-[#0a0c1b] to-[#151728] min-h-screen text-white">
          {!isAuthenticated && (
            <>
              <SheetClose asChild>
                <Link to="/login">
                  <Button variant="ghost" className="w-full justify-start text-white hover:bg-purple-500/20">
                    Sign in
                  </Button>
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link to="/register">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                    Sign up
                  </Button>
                </Link>
              </SheetClose>
            </>
          )}
          
          {isAuthenticated && (
            <>
              <SheetClose asChild>
                <Link to="/dashboard">
                  <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/10">
                    Dashboard
                  </Button>
                </Link>
              </SheetClose>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-red-500 hover:bg-white/10" 
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Signing out..." : "Log out"}
              </Button>
            </>
          )}
        </div>
      );
    }
    
    return (
      <div className="flex flex-col h-full bg-gradient-to-b from-[#0a0c1b] to-[#151728] min-h-screen text-white">
        <div className="py-6">
          <ul className="space-y-4">
            {navItems.map((item) => (
              <li key={item.name}>
                <SheetClose asChild>
                  <Link
                    to={item.path}
                    className={`block text-base font-medium py-2 px-4 transition-colors hover:bg-white/10 ${
                      location.pathname === item.path
                        ? "text-purple-400 bg-white/10"
                        : "text-white/80"
                    }`}
                  >
                    {item.name}
                  </Link>
                </SheetClose>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-auto pb-6 pt-6 border-t border-white/10">
          {isAuthenticated ? (
            <Button
              variant="default"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full font-medium bg-red-600 hover:bg-red-700 text-white mx-4"
            >
              {isLoggingOut ? "Signing out..." : "Sign Out"}
            </Button>
          ) : (
            <div className="space-y-4 px-4">
              <SheetClose asChild>
                <Link to="/login">
                  <Button variant="outline" className="w-full font-medium border-white/20 text-white hover:bg-white/10">
                    Sign In
                  </Button>
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link to="/register">
                  <Button className="w-full font-medium bg-purple-600 hover:bg-purple-700 text-white">
                    Sign Up
                  </Button>
                </Link>
              </SheetClose>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-white hover:bg-white/10">
          <User className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="right" 
        className="w-[300px] sm:w-[350px] bg-gradient-to-b from-[#0a0c1b] to-[#151728] border-none p-0"
      >
        {renderMobileNavContent()}
      </SheetContent>
    </Sheet>
  );
};

export default MobileNavSheet;
